import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import ActiveSeasonsNotice from "../../../components/activeseasonsnotice/ActiveSeasonsNotice";
import styles from "./BrandDetail.module.scss";
import { Card, Empty, Row, Col, Tag, Select, Button, Typography, Skeleton, Table, Space } from "antd";
import {
  ArrowLeftOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ShoppingCartOutlined,
  TagOutlined,
  FlagOutlined,
  AppstoreOutlined,
  FieldTimeOutlined,
  PieChartOutlined,
  ThunderboltOutlined,
  AlertOutlined,
  BulbOutlined,
} from "@ant-design/icons";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as CTitle,
  Legend,
  Tooltip as CTooltip,
  Filler,
} from "chart.js";
import {
  BrandAnalysisComplementaryBrand,
  BrandAnalysisDetailBreakdown,
  BrandAnalysisDetailChart,
  BrandAnalysisDetailCore,
  BrandAnalysisPerformanceRow,
  getAllBranchOffices,
  getAllChannels,
  getBrandAnalysisDetailBreakdown,
  getBrandAnalysisDetailChart,
  getBrandAnalysisDetailCore,
} from "../../../api/Services";
import { useFetchRequestGuard } from "../../../helpers/FilterSearchHelper";
import {
  isVariationAvailable,
  variationNotAvailableLabel,
} from "../../../helpers/VariationHelper";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  CTitle,
  Legend,
  CTooltip,
  Filler
);

const { Title, Text } = Typography;

const ALERT_I18N_KEYS: Record<string, string> = {
  SALES_DROP_VS_LY: "BrandAnalysis-Alert-SalesDropVsLY",
  PROMO_DEPENDENCY: "BrandAnalysis-Alert-PromoDependency",
  MARGIN_DECLINE: "BrandAnalysis-Alert-MarginDecline",
  TOP3_CONCENTRATION: "BrandAnalysis-Alert-Top3Concentration",
  LOW_SALES_DIVERSITY: "BrandAnalysis-Alert-LowSalesDiversity",
  OVERSTOCK: "BrandAnalysis-Alert-Overstock",
};

const CAUTION_INSIGHT_KEYS = new Set([
  "BrandAnalysis-Insight-DeclineConcentration",
  "BrandAnalysis-Insight-Top3Concentration",
  "BrandAnalysis-Insight-PromoDependency",
]);

const fmt = (v?: number | null) =>
  `$${(v ?? 0).toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
const fmtN = (v?: number | null) => (v ?? 0).toLocaleString("es-AR");

const Vr: React.FC<{ value?: number | null; suffix?: string }> = ({
  value,
  suffix = "%",
}) => {
  if (!isVariationAvailable(value)) return <span>{variationNotAvailableLabel()}</span>;
  const pos = value >= 0;
  return (
    <span
      className={`${styles.variation} ${
        pos ? styles.positive : styles.negative
      }`}
    >
      {pos ? <ArrowUpOutlined /> : <ArrowDownOutlined />}{" "}
      {pos ? "+" : ""}
      {value.toFixed(1)}
      {suffix}
    </span>
  );
};

const BrandDetailHeaderSkeleton: React.FC = () => (
  <Card className={styles.headerCard}>
    <Skeleton.Input active style={{ width: 280, height: 28, marginBottom: 16 }} />
    <Skeleton active paragraph={{ rows: 2 }} />
  </Card>
);

const BrandDetailCoreSkeleton: React.FC = () => (
  <div className={styles.kpiRow}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Card className={styles.kpiCard} key={i}>
        <Skeleton active paragraph={{ rows: 2, width: ["60%", "80%"] }} title={false} />
      </Card>
    ))}
  </div>
);

const BrandDetailChartSkeleton: React.FC = () => (
  <Row gutter={[16, 16]} className={styles.blockRow}>
    <Col xs={24} lg={16}>
      <Card className={styles.sectionCard}>
        <Skeleton.Input active size="small" style={{ width: 200, height: 16, marginBottom: 16 }} />
        <Skeleton.Input active style={{ width: "100%", height: 220 }} />
      </Card>
    </Col>
    <Col xs={24} lg={8}>
      <Card className={styles.sectionCard}>
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    </Col>
  </Row>
);

const BrandDetailBreakdownSkeleton: React.FC = () => (
  <>
    <Row gutter={[16, 16]} className={styles.blockRow}>
      {Array.from({ length: 2 }).map((_, i) => (
        <Col xs={24} lg={12} key={i}>
          <Card className={styles.sectionCard}>
            <Skeleton active paragraph={{ rows: 4 }} />
          </Card>
        </Col>
      ))}
    </Row>
    <Row gutter={[16, 16]} className={styles.blockRow}>
      {Array.from({ length: 2 }).map((_, i) => (
        <Col xs={24} lg={12} key={i}>
          <Card className={styles.sectionCard}>
            <Skeleton active paragraph={{ rows: 4 }} />
          </Card>
        </Col>
      ))}
    </Row>
    <Card className={`${styles.sectionCard} ${styles.blockRow}`}>
      <Skeleton active paragraph={{ rows: 6 }} />
    </Card>
    <Card className={`${styles.sectionCard} ${styles.blockRow}`}>
      <Skeleton active paragraph={{ rows: 6 }} />
    </Card>
    <Row gutter={[16, 16]} className={styles.blockRow}>
      <Col xs={24} lg={12}>
        <Card className={styles.sectionCard}>
          <Skeleton active paragraph={{ rows: 5 }} />
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card className={styles.sectionCard}>
          <Skeleton active paragraph={{ rows: 5 }} />
        </Card>
      </Col>
    </Row>
    <Card className={`${styles.sectionCard} ${styles.blockRow}`}>
      <Skeleton active paragraph={{ rows: 5 }} />
    </Card>
    <Row gutter={[16, 16]} className={styles.blockRow}>
      <Col xs={24} lg={12}>
        <Card className={styles.sectionCard}>
          <Skeleton active paragraph={{ rows: 5 }} />
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card className={styles.sectionCard}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      </Col>
    </Row>
  </>
);

const chartOpts = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "top" as const,
      align: "start" as const,
      labels: { usePointStyle: true, boxWidth: 6, font: { size: 11 } },
    },
  },
  scales: {
    y: {
      display: false,
      grid: { color: "rgba(0,0,0,0.05)" },
    },
    x: {
      grid: { display: false },
      ticks: { color: "#9b9bab", font: { size: 10 } },
    },
  },
};

const BrandDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const brandId = Number(id);
  const [period, setPeriod] = useState(30);
  const [channelId, setChannelId] = useState<number | undefined>();
  const [branchOfficeId, setBranchOfficeId] = useState<number | undefined>();
  const [core, setCore] = useState<BrandAnalysisDetailCore | null>(null);
  const [chart, setChart] = useState<BrandAnalysisDetailChart | null>(null);
  const [breakdown, setBreakdown] = useState<BrandAnalysisDetailBreakdown | null>(null);
  const [coreLoading, setCoreLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [breakdownLoading, setBreakdownLoading] = useState(true);
  const [channelOptions, setChannelOptions] = useState<{ value: number; label: string }[]>([]);
  const [storeOptions, setStoreOptions] = useState<{ value: number; label: string }[]>([]);
  const { beginRequest, isStaleRequest } = useFetchRequestGuard();

  useEffect(() => {
    (async () => {
      const [channels, stores] = await Promise.all([getAllChannels(), getAllBranchOffices()]);
      setChannelOptions(
        (channels || [])
          .map((c: any) => ({
            value: c.id,
            label: c?.type?.descriptionEs || c?.type?.type || c?.name || `${t("BrandDetail-Channel")} ${c.id}`,
          }))
          .filter((c: any) => c.value)
      );
      setStoreOptions(
        (stores || [])
          .map((s: any) => ({ value: s.id, label: s.name }))
          .filter((s: any) => s.value)
      );
    })();
  }, [t]);

  const fetchDetail = useCallback(async () => {
    if (!brandId) return;
    const requestId = beginRequest();
    setCoreLoading(true);
    setChartLoading(true);
    setBreakdownLoading(true);

    const params = { period, channelId, branchOfficeId };
    const coreResult = await getBrandAnalysisDetailCore(brandId, params);
    if (isStaleRequest(requestId)) return;
    setCore(coreResult);
    setCoreLoading(false);

    // Fired together and resolved independently — chart and breakdown don't
    // depend on each other, so each section paints as soon as its own
    // request comes back instead of both waiting on the slower one.
    getBrandAnalysisDetailChart(brandId, params).then((result) => {
      if (isStaleRequest(requestId)) return;
      setChart(result);
      setChartLoading(false);
    });

    getBrandAnalysisDetailBreakdown(brandId, params).then((result) => {
      if (isStaleRequest(requestId)) return;
      setBreakdown(result);
      setBreakdownLoading(false);
    });
  }, [brandId, period, channelId, branchOfficeId, beginRequest, isStaleRequest]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const chartData = useMemo(() => {
    const points = chart?.salesEvolution || [];
    return {
      labels: points.map((p) => p.label),
      datasets: [
        {
          label: t("BrandDetail-RealSales"),
          data: points.map((p) => p.salesObserved),
          borderColor: "#8b5cf6",
          backgroundColor: "rgba(139,92,246,0.08)",
          fill: true,
          tension: 0.35,
          pointRadius: 2,
          borderWidth: 3,
          spanGaps: true,
        },
        {
          label: t("BrandDetail-Forecast"),
          data: points.map((p) => p.salesForecast),
          borderColor: "rgba(139,92,246,0.35)",
          borderDash: [7, 5],
          backgroundColor: "transparent",
          tension: 0.35,
          pointRadius: 0,
          borderWidth: 3,
          spanGaps: true,
        },
      ],
    };
  }, [chart, t]);

  const buildPerfColumns = (nameTitle: string) => [
    {
      title: nameTitle,
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("BrandDetail-ColumnPresence"),
      dataIndex: "presence",
      key: "presence",
      render: (v: string) => (
        <Tag color={v === "Presente" ? "green" : "default"}>{v}</Tag>
      ),
    },
    {
      title: t("BrandDetail-Stock"),
      dataIndex: "stock",
      key: "stock",
      align: "right" as const,
      render: (v: number) => fmtN(v),
    },
    {
      title: t("BrandDetail-GrossSales"),
      key: "sales",
      align: "right" as const,
      render: (_: any, r: BrandAnalysisPerformanceRow) => (
        <div className={styles.cellStack}>
          <Text strong>{fmt(r.sales)}</Text>
          <Vr value={r.variation} />
        </div>
      ),
    },
  ];

  const channelCols = buildPerfColumns(t("BrandDetail-Channel"));
  const storeCols = buildPerfColumns(t("BrandDetail-Store"));

  const productCols = [
    {
      title: t("BrandDetail-Product"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("BrandDetail-Sale"),
      dataIndex: "sales",
      key: "sales",
      align: "right" as const,
      render: (v: number) => fmt(v),
    },
    {
      title: t("BrandDetail-Units"),
      dataIndex: "units",
      key: "units",
      align: "right" as const,
      render: (v: number) => fmtN(v),
    },
  ];

  const promotionCols = [
    {
      title: t("BrandDetail-Promotion"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("BrandDetail-Participation"),
      dataIndex: "participationPct",
      key: "participationPct",
      align: "right" as const,
      render: (v: number) => `${(v ?? 0).toFixed(1)}%`,
    },
    {
      title: t("BrandDetail-Roi"),
      dataIndex: "roi",
      key: "roi",
      align: "right" as const,
      render: (v: number | null) => (v == null ? "—" : `${v.toFixed(1)}x`),
    },
    {
      title: t("BrandDetail-Units"),
      dataIndex: "units",
      key: "units",
      align: "right" as const,
      render: (v: number) => fmtN(v),
    },
    {
      title: t("BrandDetail-Result"),
      dataIndex: "result",
      key: "result",
      align: "center" as const,
      render: (v: string) => (
        <Tag color={v === "positivo" ? "green" : "red"}>{v}</Tag>
      ),
    },
  ];

  const complementaryCols = [
    {
      title: t("BrandDetail-Brand"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("Common-Categories"),
      key: "categories",
      render: (_: any, r: BrandAnalysisComplementaryBrand) =>
        (r.categories || []).join(", ") || t("BrandDetail-NoCategory"),
    },
    {
      title: t("BrandDetail-Affinity"),
      dataIndex: "affinityPct",
      key: "affinityPct",
      align: "right" as const,
      render: (v: number) => `${(v ?? 0).toFixed(1)}%`,
    },
  ];

  const substituteCols = [
    {
      title: t("BrandDetail-Brand"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("BrandDetail-Reason"),
      dataIndex: "reason",
      key: "reason",
    },
    {
      title: t("BrandDetail-GrossSales"),
      key: "salesVar",
      align: "right" as const,
      render: (_: any, r: { salesVar?: number }) => <Vr value={r.salesVar} />,
    },
  ];

  const backBtn = (
    <Button
      icon={<ArrowLeftOutlined />}
      type="text"
      className={styles.backBtn}
      onClick={() => navigate("/commercial-analysis/brands")}
    >
      {t("BrandDetail-Brands")}
    </Button>
  );

  if (coreLoading && !core) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.content}>
          {backBtn}
          <BrandDetailHeaderSkeleton />
          <BrandDetailCoreSkeleton />
          <BrandDetailChartSkeleton />
          <BrandDetailBreakdownSkeleton />
        </div>
      </div>
    );
  }

  if (!core) {
    return (
      <div className={styles.wrapper}>
        {backBtn}
        <Empty description={t("BrandDetail-NoBrandFound")} />
      </div>
    );
  }

  const { header, kpis } = core;

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        {backBtn}

        <Card className={styles.headerCard}>
          <div className={styles.headerRow}>
            <div>
              <Title level={3} className={styles.pageTitle}>
                {header.name}
              </Title>
              <div className={styles.metaRow}>
                <div className={styles.metaItem}>
                  <PieChartOutlined className={styles.metaIcon} />
                  <span className={styles.metaLabel}>
                    {t("BrandDetail-SalesWeightLabel")}
                  </span>
                  <span className={styles.metaValue}>
                    {(header.pctGlobalSales ?? 0).toFixed(1)}%
                  </span>
                </div>
                <div className={styles.metaItem}>
                  <AppstoreOutlined className={styles.metaIcon} />
                  <span className={styles.metaLabel}>
                    {t("BrandDetail-ActiveProducts")}
                  </span>
                  <span className={styles.metaValue}>
                    {fmtN(header.activeProducts)}
                  </span>
                </div>
              </div>
            </div>
            <div className={styles.headerFilters}>
              <Select
                value={period}
                onChange={setPeriod}
                className={styles.filterSelect}
                options={[
                  { value: 30, label: t("BrandDetail-Last30Days") },
                  { value: 60, label: t("BrandDetail-Last60Days") },
                  { value: 90, label: t("BrandDetail-Last90Days") },
                  { value: 180, label: t("BrandDetail-Last180Days") },
                ]}
              />
              <Select
                placeholder={t("BrandDetail-AllChannels")}
                allowClear
                value={channelId}
                onChange={setChannelId}
                className={styles.filterSelect}
                options={channelOptions}
              />
              <Select
                placeholder={t("BrandDetail-AllStores")}
                allowClear
                value={branchOfficeId}
                onChange={setBranchOfficeId}
                className={styles.filterSelect}
                options={storeOptions}
              />
            </div>
          </div>
        </Card>

        <div className={styles.kpiRow}>
              {[
                { label: t("BrandDetail-GrossSales"), value: fmt(kpis.grossSales), variation: kpis.grossSalesVar, icon: <ShoppingCartOutlined /> },
                { label: t("BrandDetail-Units"), value: fmtN(kpis.units), variation: kpis.unitsVar, icon: <TagOutlined /> },
                { label: t("BrandDetail-Margin"), value: `${(kpis.marginPct ?? 0).toFixed(1)}%`, variation: kpis.marginVar, icon: <FlagOutlined />, suf: " pp" },
                { label: t("BrandDetail-ActiveProducts"), value: fmtN(kpis.activeProducts), variation: kpis.activeProductsVar, icon: <AppstoreOutlined /> },
                { label: t("BrandDetail-AverageTicket"), value: fmt(kpis.averageTicket), icon: <FieldTimeOutlined /> },
              ].map((item) => (
                <Card className={styles.kpiCard} key={item.label}>
                  <div className={styles.kpiIcon}>{item.icon}</div>
                  <Text className={styles.kpiLabel}>{item.label}</Text>
                  <div className={styles.kpiValue}>{item.value}</div>
                  {item.variation !== undefined && <Vr value={item.variation} suffix={item.suf || "%"} />}
                </Card>
              ))}
            </div>

        {chartLoading ? (
          <BrandDetailChartSkeleton />
        ) : (
          <Card className={`${styles.sectionCard} ${styles.blockRow}`}>
            <Title level={5} className={styles.cardTitle}>{t("BrandDetail-SalesEvolutionForecast")}</Title>
            <div className={styles.chartContainer}>
              {(chart?.salesEvolution || []).length === 0 ? (
                <Empty description={t("BrandDetail-NoEvolutionForPeriod")} />
              ) : (
                <Line data={chartData} options={chartOpts} />
              )}
            </div>
          </Card>
        )}

        {breakdownLoading ? (
          <BrandDetailBreakdownSkeleton />
        ) : (
          <>
            <Row gutter={[16, 16]} className={styles.blockRow}>
              <Col xs={24} lg={12}>
                <Card
                  className={styles.sectionCard}
                  title={
                    <Space>
                      <AlertOutlined style={{ color: "#f5222d" }} />
                      <span>{t("BrandDetail-ActiveAlerts")}</span>
                    </Space>
                  }
                >
                  <div className={styles.bulletList}>
                    {(breakdown?.alerts || []).length === 0 ? (
                      <Text type="secondary">{t("BrandDetail-NoAlertsDetected")}</Text>
                    ) : (
                      (breakdown?.alerts || []).map((a, i) => (
                        <div className={styles.bulletItem} key={i}>
                          <AlertOutlined className={styles.bulletRed} />
                          <Text>{t(ALERT_I18N_KEYS[a] ?? a)}</Text>
                        </div>
                      ))
                    )}
                  </div>
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card
                  className={styles.sectionCard}
                  title={
                    <Space>
                      <ThunderboltOutlined style={{ color: "#1890ff" }} />
                      <span>{t("BrandDetail-DetectedDrivers")}</span>
                    </Space>
                  }
                >
                  <div className={styles.bulletList}>
                    {(breakdown?.drivers || []).length === 0 ? (
                      <Text type="secondary">{t("BrandDetail-NoDriversDetected")}</Text>
                    ) : (
                      (breakdown?.drivers || []).map((d, i) => (
                        <div className={styles.bulletItem} key={i}>
                          <ThunderboltOutlined className={styles.bulletBlue} />
                          <Text>{t(d.key, d.params)}</Text>
                        </div>
                      ))
                    )}
                  </div>
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} className={styles.blockRow}>
              <Col xs={24} lg={12}>
                <Card
                  className={styles.sectionCard}
                  title={
                    <Space>
                      <BulbOutlined style={{ color: "#8b5cf6" }} />
                      <span>{t("BrandDetail-Insights")}</span>
                    </Space>
                  }
                >
                  <div className={styles.insightList}>
                    {(breakdown?.insights || []).length === 0 ? (
                      <Text type="secondary">{t("BrandDetail-NoInsightsDetected")}</Text>
                    ) : (
                      (breakdown?.insights || []).map((insight, index) => (
                        <div
                          key={index}
                          className={`${styles.insightItem} ${
                            CAUTION_INSIGHT_KEYS.has(insight.key)
                              ? styles.insightYellow
                              : styles.insightGreen
                          }`}
                        >
                          <Text>{t(insight.key, insight.params)}</Text>
                        </div>
                      ))
                    )}
                  </div>
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card
                  className={styles.sectionCard}
                  title={
                    <Space>
                      <BulbOutlined style={{ color: "#52c41a" }} />
                      <span>{t("BrandDetail-Opportunities")}</span>
                    </Space>
                  }
                >
                  <div className={styles.bulletList}>
                    {(breakdown?.opportunities || []).length === 0 ? (
                      <Text type="secondary">{t("BrandDetail-NoOpportunitiesDetected")}</Text>
                    ) : (
                      (breakdown?.opportunities || []).map((o, i) => (
                        <div className={styles.bulletItem} key={i}>
                          <BulbOutlined style={{ color: "#52c41a" }} />
                          <Text>{t(o.key, o.params)}</Text>
                        </div>
                      ))
                    )}
                  </div>
                </Card>
              </Col>
            </Row>

            <Card className={`${styles.sectionCard} ${styles.blockRow}`}>
              <Title level={5} className={styles.cardTitle}>{t("BrandDetail-ChannelPerformance")}</Title>
              <Table
                columns={channelCols as any}
                dataSource={(breakdown?.byChannel || []).map((r) => ({ ...r, key: r.id }))}
                pagination={false}
                size="small"
                className={styles.dataTable}
                locale={{ emptyText: <Empty description={t("BrandDetail-NoChannelData")} /> }}
              />
            </Card>

            <Card className={`${styles.sectionCard} ${styles.blockRow}`}>
              <Title level={5} className={styles.cardTitle}>{t("BrandDetail-StorePerformance")}</Title>
              <Table
                columns={storeCols as any}
                dataSource={(breakdown?.byStore || []).map((r) => ({ ...r, key: r.id }))}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                size="small"
                className={styles.dataTable}
                locale={{ emptyText: <Empty description={t("BrandDetail-NoStoreData")} /> }}
              />
            </Card>

            <Row gutter={[16, 16]} className={styles.blockRow}>
              <Col xs={24} lg={12}>
                <Card className={styles.sectionCard}>
                  <Title level={5} className={styles.cardTitle}>{t("BrandDetail-TopProducts")}</Title>
                  <Table
                    columns={productCols as any}
                    dataSource={(breakdown?.topProducts || []).map((p) => ({ ...p, key: p.articleId }))}
                    pagination={{ pageSize: 5, showSizeChanger: false }}
                    size="small"
                    className={styles.dataTable}
                    locale={{ emptyText: <Empty description={t("BrandDetail-NoProducts")} /> }}
                  />
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card className={styles.sectionCard}>
                  <Title level={5} className={styles.cardTitle}>{t("BrandDetail-BottomProducts")}</Title>
                  <ActiveSeasonsNotice />
                  <Table
                    columns={productCols as any}
                    dataSource={(breakdown?.bottomProducts || []).map((p) => ({ ...p, key: p.articleId }))}
                    pagination={{ pageSize: 5, showSizeChanger: false }}
                    size="small"
                    className={styles.dataTable}
                    locale={{ emptyText: <Empty description={t("BrandDetail-NoProducts")} /> }}
                  />
                </Card>
              </Col>
            </Row>

            <Card className={`${styles.sectionCard} ${styles.blockRow}`}>
              <Title level={5} className={styles.cardTitle}>{t("BrandDetail-ParticipatedPromotions")}</Title>
              <Table
                columns={promotionCols as any}
                dataSource={(breakdown?.promotions || []).map((p) => ({ ...p, key: p.id }))}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                size="small"
                className={styles.dataTable}
                scroll={{ x: 700 }}
                locale={{ emptyText: <Empty description={t("BrandDetail-NoPromotions")} /> }}
              />
            </Card>

            <Row gutter={[16, 16]} className={styles.blockRow}>
              <Col xs={24} lg={12}>
                <Card className={styles.sectionCard}>
                  <Title level={5} className={styles.cardTitle}>{t("BrandDetail-ComplementaryBrands")}</Title>
                  <Table
                    columns={complementaryCols as any}
                    dataSource={(breakdown?.complementaryBrands || []).map((r) => ({ ...r, key: r.brandId }))}
                    pagination={{ pageSize: 5, showSizeChanger: false }}
                    size="small"
                    className={styles.dataTable}
                    locale={{ emptyText: <Empty description={t("BrandDetail-NoComplementaryBrands")} /> }}
                  />
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card className={styles.sectionCard}>
                  <Title level={5} className={styles.cardTitle}>{t("BrandDetail-SubstituteBrands")}</Title>
                  <Table
                    columns={substituteCols as any}
                    dataSource={(breakdown?.substituteBrands || []).map((r) => ({ ...r, key: r.brandId }))}
                    pagination={{ pageSize: 5, showSizeChanger: false }}
                    size="small"
                    className={styles.dataTable}
                    locale={{ emptyText: <Empty description={t("BrandDetail-NoSubstituteBrands")} /> }}
                  />
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>
    </div>
  );
};

export default BrandDetail;
